import { ApiClient, ApiCurrency, ApiDomain, ApiNewProjectRequest, ApiPlatform, ApiSubscriptionStatus } from "@incendium/api";
import { Autocomplete, Box, Button, Divider, Stack, TextField, Typography } from "@mui/material";
import { initializeClientsThunk } from "Actions";
import GlassCard from "Components/GlassCard/GlassCard";
import Loading from "Components/Loading/Loading";
import SavingButton from "Components/UI/SavingButton";
import ProjectFormInner from "features/project/components/ProjectFormInner";
import { ProjectDomains } from "features/project";
import { newProject } from "features/onboarding/services/onboardingService";
import { useSubscriptions } from "features/subscription";
import { camelToText } from "Helpers/camelToText";
import { enumToArray } from "Helpers/enumToText";
import { useNotification } from "Hooks";
import { useSave } from "Hooks/useSave";
import { useCallback, useEffect, useMemo, useState } from "react";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { useDispatch, useSelector } from "react-redux";
import { clientListSelector } from "Selectors/clientListSelector";

function NewProjectPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [clientId, setClientId] = useState<number | undefined>(undefined);
  const [projectName, setProjectName] = useState("");
  const [timezone, setTimezone] = useState("");
  const [currency, setCurrency] = useState(ApiCurrency.USD);
  const [overview, setOverview] = useState("");
  const [platform, setPlatform] = useState(ApiPlatform.PLATFORM_UNSPECIFIED);
  const [domains, setDomains] = useState<ApiDomain[]>([]);
  const [subscriptionId, setSubscriptionId] = useState<string | undefined>(undefined);
  const [trialDays, setTrialDays] = useState<number | null>(null);
  const { saving, save } = useSave();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const clients = useSelector(clientListSelector);
  const dispatch = useDispatch();
  const { subscriptions, loading: sLoading } = useSubscriptions();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dispatch(initializeClientsThunk());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (clients?.hasLoaded) {
      setLoading(false);
    }
  }, [clients?.hasLoaded]);

  const activeSubscriptions = useMemo(() => {
    return subscriptions.filter(
      (s) => s.status === ApiSubscriptionStatus.SUBSCRIPTION_STATUS_ACTIVE
    );
  }, [subscriptions]);

  const selectedClient = useMemo(() => {
    if (!clientId) return null;
    return clients.list.find((c) => c.id === clientId) || null;
  }, [clients.list, clientId]);

  const selectedSubscription = useMemo(() => {
    if (!subscriptionId) return null;
    return subscriptions.find((s) => s.id === subscriptionId) || null;
  }, [subscriptions, subscriptionId]);

  const onSubmit = useCallback(() => {
    if (!clientId || !projectName || !name || !email) {
      return;
    }
    save(async () => {
      try {
        const request: ApiNewProjectRequest = {
          name,
          email,
          clientId,
          project: {
            name: projectName,
            timezone,
            currency,
            overview,
            platform,
            domains: (domains || [])
              .filter((d) => d.url && d.url !== "")
              .map((d) => ({
                domain: d.url,
                crossDomainEnabled: d.crossDomainEnabled,
              })),
          },
          subscriptionId,
          trialDays: trialDays || undefined,
        };
        await newProject(request);
        setName("");
        setEmail("");
        setClientId(undefined);
        setProjectName("");
        setTimezone("");
        setCurrency(ApiCurrency.USD);
        setOverview("");
        setPlatform(ApiPlatform.PLATFORM_UNSPECIFIED);
        setDomains([]);
        setSubscriptionId(undefined);
        setTrialDays(null);
        showSuccessNotification("Project created successfully");
      } catch (error: any) {
        let errorMessage = "Failed to create project, please try again";
        try {
          const errorData = await error.json();
          errorMessage = errorData.message;
        } catch {

        }
        showErrorNotification(errorMessage);
      }
    });
  }, [save, clientId, projectName, name, email, timezone, currency, overview, platform, domains, subscriptionId, trialDays, showSuccessNotification, showErrorNotification]);

  if (loading || sLoading) {
    return <Loading fullHeight />;
  }

  return (
    <ValidatorForm onSubmit={onSubmit}>

      <GlassCard noShadow sx={{ width: 700 }} >
        <Stack spacing={2}>
          <Typography variant="subtitle1">Create New Project</Typography>

          <Autocomplete
            id="client-picker"
            size="small"
            onChange={(e, value: ApiClient | null) => {
              setClientId(value?.id);
            }}
            value={selectedClient}
            options={clients.list}
            getOptionLabel={(o) => o.businessName || ""}
            renderInput={(params) => (
              <TextValidator
                {...params}
                label="Select a client *"
                name="client"
                variant="outlined"
                value={selectedClient ? String(selectedClient.id) : ""}
                validators={["required"]}
                errorMessages={["Client is required"]}
              />
            )}
          />

          <TextValidator
            label="User Name"
            value={name}
            variant="outlined"
            name="name"
            size="small"
            fullWidth
            validators={["required"]}
            errorMessages={["User name is required"]}
            onChange={(e: any) => setName(e.target.value)}
          />

          <TextValidator
            label="User Email"
            value={email}
            variant="outlined"
            name="email"
            size="small"
            fullWidth
            validators={["required", "isEmail"]}
            errorMessages={["Email is required", "Email is not valid"]}
            onChange={(e: any) => setEmail(e.target.value)}
          />

          <ProjectFormInner
            name={projectName}
            setName={setProjectName}
            timezone={timezone}
            setTimezone={setTimezone}
            currency={currency}
            setCurrency={setCurrency}
            overview={overview}
            setOverview={setOverview}
            disabled={false}
          />

          <Autocomplete
            id="platform-picker"
            size="small"
            onChange={(e, value) => {
              setPlatform(value || ApiPlatform.PLATFORM_UNSPECIFIED);
            }}
            value={platform}
            options={enumToArray(ApiPlatform)}
            getOptionLabel={(o) => camelToText(o)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select a platform"
                variant="outlined"
              />
            )}
          />
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Box px={0}>
          <Typography variant="subtitle2">
            Manage your projects domains
          </Typography>
          <Typography variant="body2" mb={3}>
            These are the domain you shall run incendium from
          </Typography>
          <ProjectDomains domains={domains} setDomains={setDomains} />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={2} mt={2}>
          <Autocomplete
            id="subscription-picker"
            size="small"
            onChange={(e, value) => {
              setSubscriptionId(value?.id || undefined);
            }}
            value={selectedSubscription}
            options={activeSubscriptions}
            getOptionLabel={(o) => o.name || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select a subscription"
                variant="outlined"
              />
            )}
          />
          <TextField
            label="Trial Days (optional)"
            type="number"
            value={trialDays}
            size="small"
            onChange={(e) => setTrialDays(Number(e.target.value) || null)}
          />
        </Stack>

        <Stack
          mt={4}
          direction={"row"}
          spacing={2}
          justifyContent="flex-end"
        >
          <Button
            onClick={() => {
              setName("");
              setEmail("");
              setClientId(undefined);
              setProjectName("");
              setTimezone("");
              setCurrency(ApiCurrency.USD);
              setOverview("");
              setPlatform(ApiPlatform.PLATFORM_UNSPECIFIED);
              setDomains([]);
              setSubscriptionId(undefined);
              setTrialDays(null);
            }}
            color="secondary"
          >
            Clear
          </Button>
          <SavingButton
            variant="contained"
            color="primary"
            type="submit"
            saving={saving}
          >
            Create Project
          </SavingButton>
        </Stack>
      </GlassCard>
    </ValidatorForm>
  );
}

export default NewProjectPage;
